import { assertNotEmpty, dateUtils } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'

import { useUntilChangesetKeyValue } from '../../context/useUntilChangesetKeyValue'
import { getReconciliationSpecOperationFromDate } from '../../uiModels/reconciliationSpecOperations/helpers'
import type { IndexedReconciliationSpecOperationWithMetaData } from '../../uiModels/reconciliationSpecOperations/types'
import { NO_DATE_SYMBOL } from '../../util/constants'
import { ARROW_LINE_WIDTH } from '../versionedTimeline/constants'
import { MutipleVersionCircles } from '../versionedTimeline/MutipleVersionCircles'
import { OperationVersionCircle } from '../versionedTimeline/OperationVersionCircle'
import type { useTimelineScale } from '../versionedTimeline/useTimelineScale'
import { ReconciliationSpecVersionsTooltipContent } from './ReconciliationSpecVersionsTooltipContent'
import { useIsReconciliationSpecVersionActive } from './useIsReconciliationSpecVersionActive'

type Props = {
  versions: IndexedReconciliationSpecOperationWithMetaData[]
  dateToPixel: ReturnType<typeof useTimelineScale>['dateToPixel']
}

/** The reconciliation-spec versions starting on a single date. */
export const ReconciliationSpecVersionsForDate = ({
  versions,
  dateToPixel,
}: Props) => {
  const untilChangesetKeyValue = useUntilChangesetKeyValue()
  const isVersionActive = useIsReconciliationSpecVersionActive()

  // Versions are ordered by relevance, so the last one is the version in effect on this date.
  const effectiveVersion = versions.at(-1)
  assertNotEmpty(effectiveVersion)

  const fromDate = getReconciliationSpecOperationFromDate(
    effectiveVersion.internalOperation,
  )
  const positionX = dateToPixel(
    fromDate ? dateUtils.dateUtcAsLocal(fromDate).toDate() : NO_DATE_SYMBOL,
  )
  const isAnyVersionActive = versions.some(isVersionActive)
  const isAnyVersionLockedToChangeset =
    !!untilChangesetKeyValue &&
    versions.some(
      (version) =>
        version.changesetHistory?.changesetKey === untilChangesetKeyValue,
    )

  return (
    <Tooltip
      tip={<ReconciliationSpecVersionsTooltipContent versions={versions} />}
      isInteractive
    >
      <g>
        {versions.length > 1 ? (
          <MutipleVersionCircles
            count={versions.length}
            left={positionX}
            isAnyVersionActive={isAnyVersionActive}
            isAnyVersionLockedToChangeset={isAnyVersionLockedToChangeset}
          />
        ) : (
          <OperationVersionCircle
            cy={ARROW_LINE_WIDTH / 2}
            cx={positionX}
            isActive={isAnyVersionActive}
            isDraft={effectiveVersion.isDraft}
            isInherited={false}
            isBeginningOfTime={!fromDate}
            isLockedToChangeset={isAnyVersionLockedToChangeset}
          />
        )}
      </g>
    </Tooltip>
  )
}
